import { VdFeatureManager } from '@vend/vend.js'
import merge from 'lodash/merge'
import type { VdToastNotificationService } from '../../vend-ui/types'
import { CustomersStore } from '../../factories/database/customers-store'

import { DatabaseService, Index } from '../../factories/database/database'
import { SyncManager } from '../../managers/sync-manager'
import { $http, $log, $q, $rootScope } from '../../ng-compat/imports'
import { customerName } from '../../filters/wr-customer-name'
import { CustomerManager } from '../../managers/customer-manager'

const CUSTOMER_SUGGESTION_TEMPLATE = `<react-customer-badge customer="$vdSuggestion.customer"
  size="'x-small'"
  class="vd-suggestion-badge",
  group="$vdSuggestion.customerGroup">
  </react-customer-badge>
`

/**
 * Service for accessing the customer VdSuggestionSources and VdSuggestionFormatter.  Searches via both /api/2.0/suggest
 * (typey) and by exact customer code match directly from the customers IndexedDB store.
 *
 * @class CustomerSuggestions
 * @module customerSuggestions
 */
export class CustomerSuggestions {
  private _customerCodeIndex: Index<'customers', 'byCustomerCode'>
  private _customerCodeSource: $TSFixMe
  private _customerSearchSource: $TSFixMe
  private _formatter: $TSFixMe
  private _readOnlySource: $TSFixMe
  private _source: $TSFixMe
  private _type: $TSFixMe
  private _customerSuggestionSources: $TSFixMe
  public customerErrorTemplate: $TSFixMe

  constructor(
    private VdSuggestionSource: $TSFixMe,
    VdSuggestionFormatter: $TSFixMe,
    private vdToastNotificationService: VdToastNotificationService,
    private vdFeatureManager: VdFeatureManager,
    Database: DatabaseService,
    syncManager: SyncManager,
    private customerModals: $TSFixMe,
    private customerManager: CustomerManager,
    private customersStore: CustomersStore
  ) {
    'ngInject'
    const type = 'customer'
    const entityStore = 'customers'
    const onlineOnlyCustomers = this.vdFeatureManager.get(
      'register_online_only_customers_webregister'
    )
    const SUGGEST_URL = `/api/2.0/suggest?type=customer&page_size=30&include_related=${onlineOnlyCustomers}`
    // Replaces the default error message in the autocomplete
    this.customerErrorTemplate = 'customer/customer-error.html'

    this._customerCodeIndex = Database.store(entityStore).index(
      'byCustomerCode'
    )
    this._type = type

    this._customerSearchSource = new VdSuggestionSource({
      types: [type],
      search: ({ query }: $TSFixMe) => {
        const params = { q: query }
        return $http
          .get<{ data: $TSFixMe[]; related?: { customers: $TSFixMe } }>(
            SUGGEST_URL,
            { params }
          )
          .then(({ data }) => {
            if (data.related?.customers) {
              return data.data.map((suggestion: any) => {
                return {
                  ...data.related?.customers[suggestion.id],
                  type,
                }
              })
            }

            return data.data || []
          })
      },
    })

    this._customerCodeSource = new VdSuggestionSource({
      types: [type],
      init: () => syncManager.whenCompleteEntityDataAvailable(entityStore),
      search: ({ query }: $TSFixMe) => this._customerCodeIndex.getAll(query),
      rawSuggestions: true,
    })

    this._formatter = new VdSuggestionFormatter({
      type,
      displayKey: 'displayName',
      template: CUSTOMER_SUGGESTION_TEMPLATE,
      enhance: (suggestion: $TSFixMe) => this._enhance(suggestion),
    })

    this._customerSuggestionSources = [
      this._customerCodeSource,
      this._customerSearchSource,
    ]
    if (onlineOnlyCustomers) {
      this._customerSuggestionSources = [this._customerSearchSource]
    }
    this._source = new VdSuggestionSource({
      sources: this._customerSuggestionSources,
      formatters: [this._formatter],
      feelingLucky: (options: $TSFixMe) => this._feelingLucky(options),
      create: ({ query }: $TSFixMe) =>
        this._create({ query, rawSuggestions: onlineOnlyCustomers }),
      isDuplicate: () => false,
      rawSuggestions: onlineOnlyCustomers,
    })

    this._readOnlySource = new VdSuggestionSource({
      sources: this._customerSuggestionSources,
      formatters: [this._formatter],
      feelingLucky: (options: $TSFixMe) => this._feelingLucky(options),
    })
  }

  /**
   * Get an appropriate VdSuggestionSource for the given options. By default will return a source that supports
   * creating a new customer via the create customer modal.
   *
   * @method getSource
   *
   * @param  {Object} [options]
   *
   *         @param {Boolean} [options.readOnly]
   *                If true will return a source with no option to create new customers
   *
   *         @param {Boolean} [options.customerCodeOnly]
   *                If true will return a source that only searches by exact customer code match
   *
   *         @param {Function} [options.create]
   *                If specified will return a new VdSuggestionSource configured to use this function as the create
   *                function
   *
   * @return {VdSuggestionSource}
   */
  public getSource(
    options: {
      readOnly?: boolean
      customerCodeOnly?: boolean
      create?: $TSFixMeFunction
    } = {}
  ) {
    if (options.readOnly) {
      return this._readOnlySource
    }

    if (options.customerCodeOnly) {
      return this._customerCodeSource
    }

    if (options.create) {
      return new this.VdSuggestionSource({
        sources: this._customerSuggestionSources,
        formatters: [this._formatter],
        feelingLucky: (options: $TSFixMe) => this._feelingLucky(options),
        create: options.create,
      })
    }

    return this._source
  }

  /**
   * Get the customer VdSuggestionFormatter for enhancing and rendering customer suggestions.
   *
   * @method getFormatter
   *
   * @return {VdSuggestionFormatter}
   */
  public getFormatter() {
    return this._formatter
  }

  // ============================== Private Methods ======================== //

  /**
   * Enhance a customer suggestion, hydrating the customer (if it doesn't already exist) and adding a `displayName`
   * property with an appropriately formatted name (using the vdCustomerName filter).
   *
   * @private
   * @method _enhance
   *
   * @param  {Suggestion} suggestion
   *
   * @return {Promise<Suggestion>} A promise for the enhanced suggestion
   */
  private _enhance(suggestion: $TSFixMe) {
    return $q((resolve, reject) => {
      return $q
        .resolve(suggestion.customer || this.customersStore.get(suggestion.id))
        .then(customer => {
          if (!customer) {
            reject(`Customer with id ${suggestion.id} could not be found.`)

            return $q.reject()
          }

          return $q
            .resolve(
              this.vdFeatureManager.get('customer_groups')
                ? this.customerManager.getCustomerGroup(customer)
                : undefined
            )
            .then(customerGroup => {
              return { customer, customerGroup }
            })
            .catch(error => {
              $log.warn(
                '[customerSuggestionEnhance] Failed to lookup customer group.',
                error
              )

              return { customer, customerGroup: undefined }
            })
        })
        .then(customerData => {
          const updatedSuggestion = merge({}, suggestion, {
            customer: customerData.customer,
            displayName: customerName(customerData.customer),
            customerGroup: customerData.customerGroup,
          })
          resolve(updatedSuggestion)
        })
    })
  }

  /**
   * Find a customer by exact customer code match. Displays an error notification and rejects if no customers or
   * multiple customers were found with that customer code.
   *
   * @private
   * @method _feelingLucky
   *
   * @param  {Object} options
   *
   *         @param {String} options.query
   *                The customer code that was searched for
   *
   *         @param {Suggestion} options.pendingSuggestion
   *                The active suggestion in the list that would have been selected if feelingLucky had not been
   *                triggered
   *
   *         @param {Boolean} options.pendingCreate
   *                True if the customer creation process will be triggered if no exact customer code match is found
   *
   * @return {Promise<Suggestion>} A promise that resolves to the suggestion for the found customer, or rejects if
   *         none/multiple were found
   */
  private _feelingLucky({
    query,
    pendingSuggestion,
    pendingCreate,
  }: {
    query: string
    pendingSuggestion: $TSFixMe
    pendingCreate: boolean
  }) {
    return this._customerCodeIndex.getAll(query).then((customers: $TSFixMe) => {
      let message
      if (customers.length > 1) {
        message = `More than 1 result found for "${query}". Try typing that into the search field to see results.`
      } else if (!customers.length) {
        message = `No results found for "${query}". Try searching by customer name.`
      }

      if (message) {
        if (pendingSuggestion) {
          return pendingSuggestion
        }

        if (pendingCreate) {
          return
        }

        $log.debug(`[customerSuggestionSource] ${message}`, customers)
        this.vdToastNotificationService.negative({ message })
        return
      }

      const type = this._type
      return {
        id: customers[0].id,
        type,
        [type]: customers[0],
      }
    })
  }

  /**
   * Create a new customer, pre-populating the customer's name with what the user has typed in the search so far.
   *
   * @private
   * @method _create
   *
   * @param  {Object} options
   *         @param {String} options.query The query that was searched for
   *         @param {Boolean} options.rawSuggestions Option to return the created customer already as a Suggestion entity type or its raw type
   *
   * @return {Promise<Suggestion>} A promise that resolves to the suggestion for the created customer, or rejects if
   *         customer creation was cancelled
   */
  private _create({
    query,
    rawSuggestions,
  }: {
    query: string
    rawSuggestions: boolean
  }) {
    return this.customerModals
      .createCustomer({ query })
      .then((customer: $TSFixMe) => {
        if (!rawSuggestions) {
          return {
            id: customer.id,
            type: this._type,
            customer,
          }
        }
        return customer
      })
      .catch(() => {
        $rootScope.$emit('autocomplete:customer-search:focus', {
          isFocusable: true,
          focusDelay: 200,
        })
      })
  }
}
